import React, { useState, useEffect } from 'react';
import { 
  User, Mail, Phone, Building2, Calendar, MessageSquare, 
  Clock, CheckCircle, XCircle, Loader2, LogOut, Send,
  FileText, Bell, Settings, ChevronRight
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CustomerDashboard = ({ customerData, onLogout }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [bookings, setBookings] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sendingInquiry, setSendingInquiry] = useState(false);
  const [inquiryForm, setInquiryForm] = useState({ subject: '', message: '' });
  const [inquirySuccess, setInquirySuccess] = useState('');
  const [inquiryError, setInquiryError] = useState('');

  useEffect(() => {
    fetchCustomerData();
  }, []);

  const fetchCustomerData = async () => {
    setLoading(true);
    try {
      // Fetch bookings for this customer
      const bookingsRes = await fetch(`${BACKEND_URL}/api/bookings/customer`, {
        headers: {
          'Authorization': `Bearer ${customerData.token}`
        }
      });
      
      if (bookingsRes.ok) {
        const data = await bookingsRes.json();
        setBookings(data.bookings || []);
      }

      // Fetch inquiries
      const inquiriesRes = await fetch(`${BACKEND_URL}/api/inquiries/customer`, {
        headers: {
          'Authorization': `Bearer ${customerData.token}`
        }
      });
      
      if (inquiriesRes.ok) {
        const data = await inquiriesRes.json();
        setInquiries(data.inquiries || []);
      }
    } catch (error) {
      console.error('Error fetching customer data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSendInquiry = async (e) => {
    e.preventDefault();
    if (!inquiryForm.subject.trim() || !inquiryForm.message.trim()) {
      setInquiryError('Please fill in both subject and message');
      return;
    }

    setSendingInquiry(true);
    setInquiryError('');
    setInquirySuccess('');

    try {
      const response = await fetch(`${BACKEND_URL}/api/inquiries`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${customerData.token}`
        },
        body: JSON.stringify({
          subject: inquiryForm.subject,
          message: inquiryForm.message
        })
      });

      if (response.ok) {
        setInquirySuccess('Your inquiry has been sent! We\'ll get back to you soon.');
        setInquiryForm({ subject: '', message: '' });
        fetchCustomerData(); // Refresh inquiries
      } else {
        const data = await response.json();
        setInquiryError(data.detail || 'Failed to send inquiry');
      }
    } catch (error) {
      setInquiryError('Failed to send inquiry. Please try again.');
    } finally {
      setSendingInquiry(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'confirmed': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'completed': return 'bg-blue-100 text-blue-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-[400px] max-h-[70vh] overflow-hidden flex flex-col" data-testid="customer-dashboard">
      {/* Header */}
      <div className="bg-gradient-to-r from-cyan-600 to-blue-600 px-6 py-4 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
              <User className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">{customerData.name || 'Customer'}</h2>
              <p className="text-white/80 text-sm">{customerData.email}</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onLogout}
            className="text-white hover:bg-white/20"
            data-testid="customer-logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200 bg-gray-50 px-4 flex-shrink-0">
        {[
          { id: 'overview', label: 'Overview', icon: User },
          { id: 'bookings', label: 'Bookings', icon: Calendar },
          { id: 'inquiries', label: 'Inquiries', icon: MessageSquare }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors border-b-2 -mb-px ${
              activeTab === tab.id
                ? 'text-cyan-600 border-cyan-600 bg-white'
                : 'text-gray-500 border-transparent hover:text-gray-700'
            }`}
            data-testid={`tab-${tab.id}`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-cyan-600" />
          </div>
        ) : (
          <>
            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <div className="space-y-4">
                {/* Stats Cards */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gradient-to-br from-cyan-50 to-blue-50 rounded-xl p-4 border border-cyan-100">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-cyan-100 rounded-lg">
                        <Calendar className="w-5 h-5 text-cyan-600" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-gray-900">{bookings.length}</p>
                        <p className="text-sm text-gray-600">Total Bookings</p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-4 border border-green-100">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-green-100 rounded-lg">
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-gray-900">
                          {bookings.filter(b => b.status === 'confirmed' || b.status === 'completed').length}
                        </p>
                        <p className="text-sm text-gray-600">Confirmed</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Recent Activity */}
                <div className="bg-white rounded-xl border border-gray-200 p-4">
                  <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <Clock className="w-4 h-4 text-gray-500" />
                    Recent Activity
                  </h3>
                  {bookings.length > 0 ? (
                    <div className="space-y-2">
                      {bookings.slice(0, 3).map((booking, index) => (
                        <div key={index} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                          <div>
                            <p className="font-medium text-gray-900 text-sm">{booking.service || 'Consultation'}</p>
                            <p className="text-xs text-gray-500">{formatDate(booking.date)}</p>
                          </div>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(booking.status)}`}>
                            {booking.status || 'Pending'}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-sm py-4 text-center">No bookings yet</p>
                  )}
                </div>

                {/* Quick Actions */}
                <div className="bg-white rounded-xl border border-gray-200 p-4">
                  <h3 className="font-semibold text-gray-900 mb-3">Quick Actions</h3>
                  <div className="space-y-2">
                    <button
                      onClick={() => setActiveTab('inquiries')}
                      className="w-full flex items-center justify-between p-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors"
                    >
                      <span className="flex items-center gap-2 text-sm font-medium text-gray-700">
                        <MessageSquare className="w-4 h-4 text-cyan-600" />
                        Send an Inquiry
                      </span>
                      <ChevronRight className="w-4 h-4 text-gray-400" />
                    </button>
                    <a
                      href="https://calendly.com/neura-ai/consultation"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full flex items-center justify-between p-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors"
                    >
                      <span className="flex items-center gap-2 text-sm font-medium text-gray-700">
                        <Calendar className="w-4 h-4 text-cyan-600" />
                        Book a Consultation
                      </span>
                      <ChevronRight className="w-4 h-4 text-gray-400" />
                    </a>
                  </div>
                </div>
              </div>
            )}

            {/* Bookings Tab */}
            {activeTab === 'bookings' && (
              <div className="space-y-3">
                <h3 className="font-semibold text-gray-900">Your Bookings</h3>
                {bookings.length > 0 ? (
                  bookings.map((booking, index) => (
                    <div key={index} className="bg-white rounded-xl border border-gray-200 p-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <h4 className="font-medium text-gray-900">{booking.service || 'AI Consultation'}</h4>
                          <p className="text-sm text-gray-500 mt-1">
                            <Calendar className="w-3 h-3 inline mr-1" />
                            {formatDate(booking.date)}
                          </p>
                          {booking.notes && (
                            <p className="text-sm text-gray-600 mt-2">{booking.notes}</p>
                          )}
                        </div>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(booking.status)}`}>
                          {booking.status || 'Pending'}
                        </span>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-12">
                    <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">No bookings yet</p>
                    <a
                      href="https://calendly.com/neura-ai/consultation"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-block mt-4 px-4 py-2 bg-cyan-600 text-white rounded-lg text-sm font-medium hover:bg-cyan-700"
                    >
                      Book Your First Consultation
                    </a>
                  </div>
                )}
              </div>
            )}

            {/* Inquiries Tab */}
            {activeTab === 'inquiries' && (
              <div className="space-y-4">
                {/* Send Inquiry Form */}
                <div className="bg-white rounded-xl border border-gray-200 p-4">
                  <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <Send className="w-4 h-4 text-cyan-600" />
                    Send an Inquiry
                  </h3>
                  
                  {inquirySuccess && (
                    <div className="mb-3 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm flex items-center gap-2">
                      <CheckCircle className="w-4 h-4" />
                      {inquirySuccess}
                    </div>
                  )}
                  
                  {inquiryError && (
                    <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center gap-2">
                      <XCircle className="w-4 h-4" />
                      {inquiryError}
                    </div>
                  )}
                  
                  <form onSubmit={handleSendInquiry} className="space-y-3">
                    <div>
                      <label className="text-sm font-medium text-gray-700">Subject</label>
                      <Input
                        type="text"
                        value={inquiryForm.subject}
                        onChange={(e) => setInquiryForm({...inquiryForm, subject: e.target.value})}
                        placeholder="What can we help you with?"
                        className="mt-1"
                        data-testid="inquiry-subject"
                        disabled={sendingInquiry}
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700">Message</label>
                      <textarea
                        value={inquiryForm.message}
                        onChange={(e) => setInquiryForm({...inquiryForm, message: e.target.value})}
                        placeholder="Describe your inquiry in detail..."
                        rows={4}
                        className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
                        data-testid="inquiry-message"
                        disabled={sendingInquiry}
                      />
                    </div>
                    <Button
                      type="submit"
                      disabled={sendingInquiry}
                      className="w-full bg-cyan-600 hover:bg-cyan-700"
                      data-testid="send-inquiry-btn"
                    >
                      {sendingInquiry ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Send className="w-4 h-4 mr-2" />
                          Send Inquiry
                        </>
                      )}
                    </Button>
                  </form>
                </div>

                {/* Past Inquiries */}
                <div className="bg-white rounded-xl border border-gray-200 p-4">
                  <h3 className="font-semibold text-gray-900 mb-3">Your Inquiries</h3>
                  {inquiries.length > 0 ? (
                    <div className="space-y-3">
                      {inquiries.map((inquiry, index) => (
                        <div key={index} className="border-b border-gray-100 last:border-0 pb-3 last:pb-0">
                          <div className="flex items-start justify-between">
                            <div>
                              <h4 className="font-medium text-gray-900 text-sm">{inquiry.subject}</h4>
                              <p className="text-xs text-gray-500 mt-1">{formatDate(inquiry.created_at)}</p>
                            </div>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              inquiry.status === 'responded' ? 'bg-green-100 text-green-800' :
                              inquiry.status === 'closed' ? 'bg-gray-100 text-gray-800' :
                              'bg-yellow-100 text-yellow-800'
                            }`}>
                              {inquiry.status || 'Pending'}
                            </span>
                          </div>
                          <p className="text-sm text-gray-600 mt-2 line-clamp-2">{inquiry.message}</p>
                          {inquiry.response && (
                            <div className="mt-2 p-2 bg-cyan-50 rounded-lg">
                              <p className="text-xs font-medium text-cyan-700">Response:</p>
                              <p className="text-sm text-gray-700">{inquiry.response}</p>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-sm text-center py-4">No inquiries yet</p>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default CustomerDashboard;
